/**
 * Collection page functionality
 * Handles quantity selectors with automatic add/remove from cart
 */

(function() {
  'use strict';

  let cartData = null;

  // Load cart data
  async function loadCart() {
    try {
      const response = await fetch('/cart.js');
      cartData = await response.json();
      return cartData;
    } catch (error) {
      console.error('Error loading cart:', error);
      return null;
    }
  }

  // Get current quantity of a variant in cart
  function getCartQuantity(variantId) {
    if (!cartData || !cartData.items) return 0;
    const item = cartData.items.find(item => item.variant_id === variantId);
    return item ? item.quantity : 0;
  }

  // Add item to cart
  async function addToCart(variantId, quantity) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erro ao adicionar ao carrinho');
      }

      // Reload cart data
      await loadCart();
      await updateCartCount();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  // Update item quantity in cart
  async function updateCartQuantity(variantId, quantity) {
    try {
      // Find the line item key
      if (!cartData || !cartData.items) return false;
      const item = cartData.items.find(item => item.variant_id === variantId);
      if (!item) return false;

      const formData = new FormData();
      formData.append('id', item.key);
      formData.append('quantity', quantity);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erro ao atualizar carrinho');
      }

      // Reload cart data
      await loadCart();
      await updateCartCount();
      return true;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  // Remove item from cart
  async function removeFromCart(variantId) {
    try {
      // Find the line item key
      if (!cartData || !cartData.items) return false;
      const item = cartData.items.find(item => item.variant_id === variantId);
      if (!item) return false;

      const formData = new FormData();
      formData.append('id', item.key);
      formData.append('quantity', 0);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erro ao remover do carrinho');
      }

      // Reload cart data
      await loadCart();
      await updateCartCount();
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  // Update cart count in header
  async function updateCartCount() {
    try {
      const cartCountElement = document.querySelector('.header__cart-count');
      if (cartCountElement && cartData) {
        if (cartData.item_count > 0) {
          cartCountElement.textContent = cartData.item_count;
          cartCountElement.style.display = '';
        } else {
          cartCountElement.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // Initialize quantity selectors
  async function initQuantitySelectors() {
    // Load cart first
    await loadCart();

    const quantitySelectors = document.querySelectorAll('.menu-selection__quantity-selector');
    
    quantitySelectors.forEach(selector => {
      const minusBtn = selector.querySelector('.menu-selection__quantity-btn--minus');
      const plusBtn = selector.querySelector('.menu-selection__quantity-btn--plus');
      const input = selector.querySelector('.menu-selection__quantity-input');
      const variantId = parseInt(selector.dataset.variantId, 10);
      const productId = selector.dataset.productId;
      
      if (!minusBtn || !plusBtn || !input || !variantId) return;

      // Set initial quantity from cart
      const initialQuantity = getCartQuantity(variantId);
      input.value = initialQuantity;

      // Disable minus button if quantity is 0
      if (initialQuantity === 0) {
        minusBtn.disabled = true;
        minusBtn.style.opacity = '0.5';
      }

      // Plus button - add to cart
      plusBtn.addEventListener('click', async () => {
        const currentValue = parseInt(input.value, 10) || 0;
        const newValue = currentValue + 1;
        
        // Disable buttons during update
        plusBtn.disabled = true;
        minusBtn.disabled = true;
        
        try {
          if (currentValue === 0) {
            // Add new item to cart
            await addToCart(variantId, 1);
          } else {
            // Update existing item quantity
            await updateCartQuantity(variantId, newValue);
          }
          
          input.value = newValue;
          minusBtn.disabled = false;
          minusBtn.style.opacity = '1';
        } catch (error) {
          console.error('Error updating cart:', error);
          window.toast('Erro ao adicionar ao carrinho. Por favor, tente novamente.', 'error');
        } finally {
          plusBtn.disabled = false;
        }
      });

      // Minus button - decrease quantity or remove
      minusBtn.addEventListener('click', async () => {
        const currentValue = parseInt(input.value, 10) || 0;
        if (currentValue <= 0) return;
        
        const newValue = currentValue - 1;
        
        // Disable buttons during update
        plusBtn.disabled = true;
        minusBtn.disabled = true;
        
        try {
          if (newValue === 0) {
            // Remove item from cart
            await removeFromCart(variantId);
            input.value = 0;
            minusBtn.disabled = true;
            minusBtn.style.opacity = '0.5';
          } else {
            // Update quantity
            await updateCartQuantity(variantId, newValue);
            input.value = newValue;
          }
        } catch (error) {
          console.error('Error updating cart:', error);
          window.toast('Erro ao atualizar carrinho. Por favor, tente novamente.', 'error');
        } finally {
          plusBtn.disabled = false;
          if (newValue > 0) {
            minusBtn.disabled = false;
          }
        }
      });
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initQuantitySelectors();
    });
  } else {
    initQuantitySelectors();
  }
})();
